// What the Event weights page says about one event, derived from the statistics
// the feature evaluation already carries.
//
// Every reading here is arithmetic over fields `getFeatureEvaluation` returns
// today (lift, did_X, the per-lead averages, the activity flag), so the page
// needs no new route to show it. The suggestion rules live in one place, with
// the sentence that explains each suggestion built beside the number it
// explains: a suggestion a person cannot trace back to the bar they are
// looking at is one they will not take, and the old column was exactly that
// (`ceil(lift / max(avgConverted - avgNonConverted, 1))`, a unit-mixing
// heuristic nobody could read off the row).
//
// Where statistics run out the page says so in words and hands the decision
// over, rather than greying the row. An event with six doers is not a worse
// event than one with six hundred; it is one the model has not seen enough of,
// and the weight it gets is a business judgement. "Use statistics where
// possible, judgement where needed" is the whole stance of this page.

import { Feature } from '../../../models/feature_eval/FeatureEvaluation';
import { formatLift } from '../trees/nodeLift';

/**
 * Doers at or above which the lift is read as reliable. The same line the tree
 * page draws for a split (`MIN_SPLIT_POPULATION`), so "reliable" means one
 * thing across the product.
 */
export const RELIABLE_DOERS = 100;
/** Below this many doers no weight is suggested at all. */
export const LIMITED_DOERS = 10;

/**
 * Occurrences per converting lead above which the event is too frequent to
 * weight per occurrence: at importance 1, twenty opens outscore one demo
 * request. The suggestion is then 0 and an aggregated event takes its place,
 * which the model builder agent sets up; this page only points at it.
 */
export const FREQUENT_OCCURRENCES = 10;
/**
 * The number of occurrences a lifespan is sized to hold. A converter who does
 * an event three times in 90 days keeps the 90; one who does it thirty times
 * gets a window a tenth as long, so the score stops piling up.
 */
export const LIFESPAN_TARGET_OCCURRENCES = 3;
export const LIFESPAN_MIN = 7;
export const LIFESPAN_MAX = 90;
/**
 * The lifespans a person may pick, longest first. A fixed ladder rather than
 * any number of days: a score is simulated and explained in terms of these
 * windows, and twenty events each on a window of their own is a score nobody
 * can reason about. A stored value off the ladder (from the old free field) is
 * kept as it is until somebody changes it.
 */
export const LIFESPAN_STEPS = [90, 75, 60, 45, 30, 15, 7];

/** The ladder step nearest a number of days, the longer one on a tie. */
export function snapLifespan(days: number): number {
  return LIFESPAN_STEPS.reduce((best, step) =>
    Math.abs(step - days) < Math.abs(best - days) ? step : best
  );
}

/** Importance a reliable event may be suggested, either side of zero. */
export const IMPORTANCE_MIN = -20;
export const IMPORTANCE_MAX = 30;
/** Importance an event with limited evidence may be suggested, either side. */
export const IMPORTANCE_CAP_LIMITED = 10;
/** The least a flagged negative event is pushed below zero. */
export const NEGATIVE_FLOOR = 5;

export type Confidence = 'reliable' | 'limited' | 'few' | 'none';

export interface EventReading {
  /** Conversion rate of doers over the baseline, as a multiple. Null with no doers. */
  multiple: number | null;
  /** "11.0x", or "no stats" when there were no doers to measure. */
  multipleLabel: string;
  confidence: Confidence;
  /** The confidence as the row prints it. */
  confidenceLabel: string;
  /** Why the confidence is what it is, for the tooltip. */
  confidenceTitle: string;
  /** Average occurrences per converting lead who did the event. */
  occurrences: number;
  frequent: boolean;
  /** Where the occurrences dot sits on the log axis, 0 to 100. */
  occurrencesPercent: number;
  /** Null when the statistics do not support a suggestion. */
  suggestedImportance: number | null;
  /** The arithmetic behind the suggestion, or why there is none. */
  importanceWhy: string;
  /** Null when the event is frequent (importance 0 makes the window moot). */
  suggestedLifespan: number | null;
  lifespanWhy: string;
}

export const clamp = (value: number, low: number, high: number) =>
  Math.max(low, Math.min(high, value));

/** The stored lift is `rate / baseline - 1`; the product reads multiples. */
export function liftMultiple(
  feature: Pick<Feature, 'lift' | 'didX'>
): number | null {
  if (!Number(feature.didX)) return null;
  const multiple = Number(feature.lift) + 1;
  return Number.isFinite(multiple) ? Math.max(0, multiple) : null;
}

export function confidenceOf(doers: number): Confidence {
  if (!doers) return 'none';
  if (doers >= RELIABLE_DOERS) return 'reliable';
  if (doers >= LIMITED_DOERS) return 'limited';
  return 'few';
}

const CONFIDENCE_LABEL: Record<Confidence, string> = {
  reliable: 'Reliable',
  limited: 'Some evidence',
  few: 'Use judgement',
  none: 'Use judgement',
};

/** 10 x ln(multiple): 2x gives +7, 4x gives +14, 10x gives +23, 0.5x gives -7. */
export function importanceFromMultiple(multiple: number): number {
  if (multiple <= 0) return IMPORTANCE_MIN;
  return Math.round(10 * Math.log(multiple));
}

/** The largest occurrence count the axis shows; beyond it the dot sits at the end. */
export const OCCURRENCES_AXIS_MAX = 40;

export function occurrencesPercent(occurrences: number): number {
  const bounded = clamp(occurrences, 1, OCCURRENCES_AXIS_MAX);
  return (Math.log10(bounded) / Math.log10(OCCURRENCES_AXIS_MAX)) * 100;
}

const signed = (value: number) => (value > 0 ? `+${value}` : `${value}`);
const times = (value: number) => `${value.toFixed(1)} times`;

export function readEvent(feature: Feature): EventReading {
  const doers = Number(feature.didX) || 0;
  const occurrences = Number(feature.averageForConverted) || 0;
  const negative = feature.negativeUserActivity === 'yes';
  const multiple = liftMultiple(feature);
  const confidence = confidenceOf(doers);
  // Only read off enough doers: a frequency measured on four leads is as
  // thin as a lift measured on four, and must not turn into a 0.
  const measured = confidence === 'reliable' || confidence === 'limited';
  const frequent = measured && occurrences > FREQUENT_OCCURRENCES;

  const confidenceTitle =
    confidence === 'none'
      ? 'No lead in the training set did this event.'
      : `${doers.toLocaleString('en-US')} leads did this event. ${
          confidence === 'reliable'
            ? `From ${RELIABLE_DOERS} leads the lift is read as reliable.`
            : confidence === 'limited'
            ? `Between ${LIMITED_DOERS} and ${RELIABLE_DOERS} the lift is a hint, so suggestions are capped.`
            : `Under ${LIMITED_DOERS} the statistics say little, so no weight is suggested.`
        }`;

  let suggestedImportance: number | null = null;
  let importanceWhy: string;
  const judgement =
    'Set the weight from what you know about this event in your funnel.';

  if (confidence === 'none') {
    importanceWhy = `No lead did this event in the training set. ${judgement}`;
  } else if (frequent) {
    suggestedImportance = 0;
    importanceWhy = `Converting leads did it ${times(
      occurrences
    )} each, over the cutoff of ${FREQUENT_OCCURRENCES}. Per occurrence it would swamp the score: weight an aggregated event instead.`;
  } else if (confidence === 'few') {
    importanceWhy = `Only ${doers} leads did this event, too few to read a lift from. ${judgement}`;
  } else {
    const raw = importanceFromMultiple(multiple ?? 0);
    const cap = confidence === 'limited' ? IMPORTANCE_CAP_LIMITED : null;
    let value = cap
      ? clamp(raw, -cap, cap)
      : clamp(raw, IMPORTANCE_MIN, IMPORTANCE_MAX);
    if (negative) value = -Math.max(Math.abs(value), NEGATIVE_FLOOR);
    suggestedImportance = value;

    importanceWhy = `${formatLift(multiple)} lift gives ${signed(raw)} points`;
    if (cap && Math.abs(raw) > cap) {
      importanceWhy += `, capped at ${signed(
        value
      )} because the evidence is limited (${doers} leads)`;
    } else if (!negative && value !== raw) {
      importanceWhy += `, capped at ${signed(value)}`;
    } else if (negative && Math.abs(value) < Math.abs(raw)) {
      importanceWhy += `, capped at ${value}`;
    }
    if (negative) {
      importanceWhy += '. Flagged as negative activity, so it stays below zero';
    }
    importanceWhy += '.';
  }

  let suggestedLifespan: number | null = null;
  let lifespanWhy: string;
  if (confidence === 'none') {
    lifespanWhy = 'No occurrences to size a window from.';
  } else if (frequent) {
    lifespanWhy = 'At importance 0 the window does not matter.';
  } else if (!occurrences) {
    // Doers, none of whom converted: there is no converter to size a window
    // on, so the full window stands.
    suggestedLifespan = LIFESPAN_MAX;
    lifespanWhy =
      'No converting lead did this event, so the full window stands.';
  } else if (occurrences <= LIFESPAN_TARGET_OCCURRENCES) {
    suggestedLifespan = LIFESPAN_MAX;
    lifespanWhy = `Converting leads did it ${times(
      occurrences
    )} in 90 days, so the full window holds about ${LIFESPAN_TARGET_OCCURRENCES} occurrences.`;
  } else {
    suggestedLifespan = snapLifespan(
      clamp(
        Math.round((LIFESPAN_MAX * LIFESPAN_TARGET_OCCURRENCES) / occurrences),
        LIFESPAN_MIN,
        LIFESPAN_MAX
      )
    );
    lifespanWhy = `Converting leads did it ${times(
      occurrences
    )} in 90 days; ${suggestedLifespan} days holds about ${LIFESPAN_TARGET_OCCURRENCES} of them.`;
  }

  return {
    multiple,
    multipleLabel: multiple === null ? 'no stats' : formatLift(multiple),
    confidence,
    confidenceLabel: CONFIDENCE_LABEL[confidence],
    confidenceTitle,
    occurrences,
    frequent,
    occurrencesPercent: occurrencesPercent(occurrences),
    suggestedImportance,
    importanceWhy,
    suggestedLifespan,
    lifespanWhy,
  };
}
